import classnames from "classnames";

import {Flag} from "@/components/Flag/Flag.tsx";
import {EmptyTable} from "@/components/table/subComponents/EmptyTable/EmptyTable.tsx";
import {LimitAndOffset} from "@/components/table/subComponents/Paginator/Paginator.tsx";
import {Table} from "@/components/table/Table.tsx";
import {ColumnSpec, Sorting, SortingDirection} from "@/components/table/Table.types.ts";
import {AccountSubscriptionType} from "@/lib/types/accounts.ts";
import {MonitoringGroupLevel, MonitoringServer} from "@/lib/types/monitoring.ts";
import {MonitoringServerRow} from "@/pages/monitoring/monitoringservers/MonitoringServerRow.tsx";
import {UpgradeButton} from "@/pages/monitoring/monitoringservers/UpgradeButton.tsx";

import {isAvailable} from "../utils.ts";

export const MonitoringGroupLevelLabel: Record<MonitoringGroupLevel, string> = {
  [MonitoringGroupLevel.LEVEL_0]: "Starter",
  [MonitoringGroupLevel.LEVEL_1]: "Starter",
  [MonitoringGroupLevel.LEVEL_2]: "Essential",
  [MonitoringGroupLevel.LEVEL_3]: "Premium",
};

type PublicProbeServersTableProps = {
  monitoringServers: MonitoringServer[];
  sortColumn: string;
  sortDirection: SortingDirection;
  onSortingChange?: (sorting: Sorting) => void;
  loading?: boolean;
  billingType?: AccountSubscriptionType;
  monitoringLevel?: MonitoringGroupLevel;
  pagination?: LimitAndOffset;
  totalRows?: number;
  updatePagination?: (laf: LimitAndOffset) => void;
  paginationData?: unknown[];
};

export const MonitoringServersTable = ({
  monitoringServers,
  sortColumn,
  sortDirection,
  onSortingChange,
  loading,
  billingType,
  monitoringLevel,
  pagination,
  totalRows,
  updatePagination,
  paginationData,
}: PublicProbeServersTableProps) => {
  const anyUnavailable = monitoringServers.some((server) => !isAvailable(server, monitoringLevel));

  const specs: ColumnSpec<MonitoringServer>[] = [
    ...[
      {
        fieldName: "name",
        fieldTitle: "Probe Server",
        enableSorting: true,
        accessor: (server: MonitoringServer) => {
          return (
            <MonitoringServerRow
              server={server}
              userMonitoringLevel={monitoringLevel}
              className={"pulseTextBodySmallBold"}
            >
              {server.name}
            </MonitoringServerRow>
          );
        },
      },
      {
        fieldName: "tier",
        fieldTitle: "Tier",
        accessor: (server: MonitoringServer) => {
          return (
            <MonitoringServerRow
              server={server}
              userMonitoringLevel={monitoringLevel}
              className={"pulseTextBodySmall pulse-color-gray-700"}
            >
              {server.monitoring_level && MonitoringGroupLevelLabel[server.monitoring_level]}
            </MonitoringServerRow>
          );
        },
      },
      {
        fieldName: "location",
        fieldTitle: "Location",
        accessor: (server: MonitoringServer) => {
          return (
            <MonitoringServerRow server={server} userMonitoringLevel={monitoringLevel}>
              <Flag className="me-1" country={server.country} withBorder />
              <span>{server.location}</span>
            </MonitoringServerRow>
          );
        },
      },
      {
        fieldName: "ipv4",
        fieldTitle: "IPv4 Address",
        accessor: (server: MonitoringServer) => {
          return (
            <MonitoringServerRow
              server={server}
              userMonitoringLevel={monitoringLevel}
              className={classnames("me-3 text-wrap d-flex flex-column")}
            >
              {server.address_ipv4_list.map((ip) => (
                <span key={ip}>{ip}</span>
              ))}
              {server.address_ipv4_list.length < 1 && "n/a"}
            </MonitoringServerRow>
          );
        },
      },
      {
        fieldName: "ipv6",
        fieldTitle: "IPv6 Address",
        accessor: (server: MonitoringServer) => {
          return (
            <MonitoringServerRow
              server={server}
              userMonitoringLevel={monitoringLevel}
              className={classnames("me-3 text-wrap d-flex flex-column")}
            >
              {server.address_ipv6_list.map((ip) => (
                <span key={ip}>{ip}</span>
              ))}
              {server.address_ipv6_list.length < 1 && "n/a"}
            </MonitoringServerRow>
          );
        },
      },
    ],
    ...(anyUnavailable
      ? [
          {
            fieldName: "upgrade",
            fieldTitle: "",
            accessor: (server: MonitoringServer) => {
              if (isAvailable(server, monitoringLevel)) return null;
              return (
                <UpgradeButton
                  billingType={billingType || AccountSubscriptionType.BILLING_1_0}
                  groupId={server.group_id}
                />
              );
            },
          },
        ]
      : []),
  ];

  return (
    <>
      <Table
        rows={monitoringServers}
        minWidth="800px"
        sorting={{column: sortColumn, direction: sortDirection}}
        onSortingChange={onSortingChange}
        isLoading={loading}
        specs={specs}
        pagination={pagination}
        totalRows={totalRows}
        updatePagination={updatePagination}
        paginationData={paginationData}
        noDataComponent={<EmptyTable>No Public Probe Servers found matching the filter(s) above.</EmptyTable>}
      />
    </>
  );
};
